import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { colorOf } from "@/lib/habitColors";
import { toast } from "sonner";
import { CalendarCheck, ChevronDown, ChevronUp, Check, Star, BookOpen, ArrowUp, Inbox, Mail, HeartPulse } from "lucide-react";

export function WeeklyDigest({ digest, onChange }) {
  const [open, setOpen] = useState(digest?.show_by_default ?? false);
  const [sending, setSending] = useState(false);
  if (!digest) return null;
  const range = `${new Date(digest.start).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(digest.end).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  const toggleEmail = async () => {
    const r = await api.patch("/digest/email", { enabled: !digest.email_enabled });
    toast(r.data.enabled ? "Sunday digest emails on." : "Digest emails off. The in-app recap stays.");
    onChange?.();
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/digest/send");
      toast.success(`Sent to ${r.data.to}. Check your inbox.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="warm-card overflow-hidden" data-testid="weekly-digest">
      <button
        data-testid="digest-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-700 text-white flex items-center justify-center"><CalendarCheck className="w-5 h-5" /></div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Your week in review · {range}</div>
            <div className="font-display text-lg font-bold text-stone-900" data-testid="digest-headline">{digest.headline}</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
      </button>

      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 pb-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Check-ins", v: digest.checkins, id: "checkins" },
              { label: "Points earned", v: `+${digest.points}`, id: "points" },
              { label: "Goals met", v: `${digest.goals_met}/${digest.goals.length}`, id: "goals" },
              { label: "Notes received", v: digest.notes_received, id: "notes" },
            ].map((m) => (
              <div key={m.id} className="rounded-2xl bg-stone-50 p-3 text-center" data-testid={`digest-stat-${m.id}`}>
                <div className="font-mono text-2xl font-bold text-amber-800">{m.v}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{m.label}</div>
              </div>
            ))}
          </div>

          {digest.goals.length > 0 && (
            <ul className="space-y-1.5">
              {digest.goals.map((g) => (
                <li key={g.habit} className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-2 text-stone-800">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center ${g.met ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-400"}`}>
                      {g.met ? <Check className="w-3 h-3" /> : null}
                    </span>
                    {g.habit}
                  </span>
                  <span className="font-mono text-xs text-stone-500">{g.count}/{g.goal}</span>
                </li>
              ))}
            </ul>
          )}

          {digest.stages?.length > 0 && (
            <ul className="space-y-1" data-testid="digest-stage-lines">
              {digest.stages.map((s) => (
                <li key={s.habit_id} className="flex items-center gap-2 text-sm text-stone-700" data-testid={`digest-stage-${s.habit_id}`}>
                  <span className={`w-2 h-2 rounded-full ${colorOf({ color: s.color }).dot}`} />
                  <span>{s.text}</span><span className="text-xs text-stone-400">· {s.title}</span>
                </li>
              ))}
            </ul>
          )}

          {(digest.level_ups.length > 0 || digest.achievements.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {digest.level_ups.map((l, i) => (
                <span key={`l${i}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800">
                  <ArrowUp className="w-3 h-3" /> {l.habit} → {l.new_goal.toLowerCase()}
                </span>
              ))}
              {digest.achievements.map((a, i) => (
                <span key={`a${i}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-800">
                  <Star className="w-3 h-3 fill-amber-500" /> {a.title} +{a.points}
                </span>
              ))}
            </div>
          )}

          {digest.reflections.length > 0 && (
            <div className="text-xs text-stone-600 flex items-start gap-2">
              <Inbox className="w-3.5 h-3.5 mt-0.5 text-stone-400" />
              <span>
                What got in the way: {digest.reflections.map((r) => `${r.habit} — ${r.reason}`).join("; ")}. Knowing this is how we adjust.
              </span>
            </div>
          )}

          {digest.wellbeing?.logs > 0 && (
            <div className="pt-3 border-t border-stone-100" data-testid="digest-wellbeing">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-2">
                <HeartPulse className="w-3.5 h-3.5" /> Wellbeing · {digest.wellbeing.logs} check-in{digest.wellbeing.logs === 1 ? "" : "s"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {digest.wellbeing.scales.map((s) => (
                  <span key={s.key} className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-700" data-testid={`digest-scale-${s.key}`}>
                    {s.label} <span className="font-mono font-bold text-stone-900">{s.avg}</span>
                    {s.delta != null && Math.abs(s.delta) >= 0.5 && (
                      <span className={`ml-1 font-mono ${s.improved ? "text-emerald-700" : "text-stone-400"}`}>
                        {s.delta > 0 ? "▲" : "▼"}{Math.abs(s.delta)}
                      </span>
                    )}
                  </span>
                ))}
              </div>
              {digest.wellbeing.improved.length > 0 && (
                <p className="text-xs text-emerald-800 mt-2" data-testid="digest-improved">Better than the week before: {digest.wellbeing.improved.join(", ")}.</p>
              )}
              {digest.wellbeing.trackers.length > 0 && (
                <ul className="mt-2 space-y-0.5" data-testid="digest-trackers">
                  {digest.wellbeing.trackers.map((t) => (
                    <li key={t.tracker_id} className="text-xs text-stone-600"><span className="font-semibold text-stone-800">{t.name}</span> — {t.summary}</li>
                  ))}
                </ul>
              )}
              {digest.wellbeing.resilience_bonuses > 0 && (
                <p className="text-xs text-amber-800 mt-2 font-medium" data-testid="digest-resilience">
                  {digest.wellbeing.resilience_bonuses} Resilience bonus{digest.wellbeing.resilience_bonuses === 1 ? "" : "es"} — you kept going on hard days. That counts double.
                </p>
              )}
            </div>
          )}

          {digest.suggested_read && (
            <Link
              to={`/learn?article=${digest.suggested_read.id}`}
              data-testid="digest-suggested-read"
              className="block rounded-2xl p-4 bg-gradient-to-r from-amber-50 to-emerald-50 border border-amber-200 hover:border-amber-400 transition"
            >
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800">
                <BookOpen className="w-3.5 h-3.5" /> A read for this week · {digest.suggested_read.why}
              </div>
              <div className="font-display font-bold text-stone-900 mt-1">{digest.suggested_read.title}</div>
              <div className="text-xs text-stone-600 mt-0.5">{digest.suggested_read.summary}</div>
            </Link>
          )}
          {digest.email_available && (
            <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-stone-100" data-testid="digest-email-row">
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input type="checkbox" data-testid="digest-email-toggle" checked={!!digest.email_enabled} onChange={toggleEmail} className="accent-amber-600 w-4 h-4" />
                <Mail className="w-4 h-4 text-stone-500" /> Email me this recap every Sunday evening
              </label>
              <button data-testid="digest-send-now" onClick={sendNow} disabled={sending} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-900 hover:text-white transition disabled:opacity-50">
                {sending ? "Sending…" : "Email me this now"}
              </button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
